import {
  Box,
  IconButton,
  Button,
  Input,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Form } from "react-bootstrap";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import AddIcon from "@mui/icons-material/Add";

import "react-toastify/dist/ReactToastify.css";
import Nodata from "../../../assets/errors/nodata.png"; // Adjust the path as necessary

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [department, setDepartment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    project_type: "",
    department: "",
    name: "",
    description: "",
    general_link: "",
    android_link: "",
    ios_link: "",
    image: "",
  });

  // Project type options
  const projectTypes = [
    { value: "mobile", label: "Mobile" },
    { value: "web", label: "Web" },
    { value: "others", label: "Others" },
  ];

  useEffect(() => {
    async function fetchData() {
      try {
        const responseDepartment = await getData("proccess.php", "department"); // Fetch department data
        let deptData = [];
        if (responseDepartment.success) {
          deptData = responseDepartment.data;
          setDepartment(deptData);
        } else {
          toast.error(
            responseDepartment.message || "Failed to load department data"
          );
        }

        const response = await getData("proccess.php", "projects_V2"); // Fetch projects data
        if (response.success) {
          const projectsWithDept = response.data.map((proj) => {
            const dept = deptData.find((d) => d.id == proj.department);
            return {
              ...proj,
              department_name: dept ? dept.department_name : proj.department,
              project_type_label:
                projectTypes.find((pt) => pt.value === proj.project_type)
                  ?.label || proj.project_type,
            };
          });
          setProjects(projectsWithDept);
        } else {
          toast.error(response.message || "Failed to load project data");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch projects");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getData]);

  const handleAddClick = () => {
    setFormData({
      project_type: "",
      department: "",
      name: "",
      description: "",
      general_link: "",
      android_link: "",
      ios_link: "",
      image: "",
    });
    setOpenAddDialog(true);
  };

  const handleEditClick = (project) => {
    setSelectedProject(project);
    setFormData({
      project_type: project.project_type || "",
      department: project.department || "",
      name: project.name || "",
      description: project.description || "",
      general_link: project.general_link || "",
      android_link: project.android_link || "",
      ios_link: project.ios_link || "",
      image: project.image || "",
    });
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteData(
        "proccess.php",
        "projects_V2",
        projectToDelete.id
      ); // Delete project by ID
      if (response.success) {
        toast.success(`Project deleted successfully`);
        setProjects(
          projects.filter((project) => project.id !== projectToDelete.id)
        );
      } else {
        toast.error(response.message || "Failed to delete project");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete project");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setProjectToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      image: e.target.files[0],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (selectedProject) {
        // Update existing project
        response = await updateData(
          "proccess.php",
          "projects_V2",
          formData,
          selectedProject.id
        ); // Update project
        if (response.success) {
          toast.success(`Project updated successfully`);
          const projectsWithDept = response.data.map((proj) => {
            const dept = department.find((d) => d.id == proj.department);
            return {
              ...proj,
              department_name: dept ? dept.department_name : proj.department,
              project_type_label:
                projectTypes.find((pt) => pt.value === proj.project_type)
                  ?.label || proj.project_type,
            };
          });
          setProjects(projectsWithDept);
        }
      } else {
        // Add new project
        response = await addData("proccess.php", "projects_V2", formData); // Add project
        if (response.success) {
          toast.success(`Project added successfully`);
          const projectsWithDept = response.data.map((proj) => {
            const dept = department.find((d) => d.id == proj.department);
            return {
              ...proj,
              department_name: dept ? dept.department_name : proj.department,
              project_type_label:
                projectTypes.find((pt) => pt.value === proj.project_type)
                  ?.label || proj.project_type,
            };
          });
          setProjects(projectsWithDept);
        }
      }

      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedProject(null);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 20 },
    {
      field: "project_type_label",
      headerName: "Project Type",
      flex: 0.8,
      minWidth: 100,
    },
    {
      field: "department_name",
      headerName: "Department",
      flex: 1,
      minWidth: 120,
    },
    { field: "name", headerName: "Name", flex: 1, minWidth: 120 },
    { field: "description", headerName: "Description", flex: 1, minWidth: 120 },
    {
      field: "general_link",
      headerName: "General Link",
      flex: 1,
      minWidth: 120,
      renderCell: (params) =>
        params.value ? (
          <a
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: colors.blue[400], textDecoration: "none" }}
          >
            View Link
          </a>
        ) : (
          "No link"
        ),
    },
    {
      field: "android_link",
      headerName: "Android Link",
      flex: 1,
      minWidth: 120,
      renderCell: (params) =>
        params.value ? (
          <a
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: colors.blue[400], textDecoration: "none" }}
          >
            Play Store
          </a>
        ) : (
          "No link"
        ),
    },
    {
      field: "ios_link",
      headerName: "iOS Link",
      flex: 1,
      minWidth: 120,
      renderCell: (params) =>
        params.value ? (
          <a
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: colors.blue[400], textDecoration: "none" }}
          >
            App Store
          </a>
        ) : (
          "No link"
        ),
    },
    {
      field: "image",
      headerName: "Project Image",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box width={50} height={50}>
          {params.value ? (
            <img
              src={`https://mindsinaction.com.na/assets/media/pictures/${params.value}`}
              alt={params.value || "Project"}
              style={{
                width: 50,
                height: 50,
                objectFit: "cover",
                cursor: "pointer",
              }}
              onClick={() =>
                window.open(
                  `https://mindsinaction.com.na/assets/media/pictures/${params.value}`,
                  "_blank"
                )
              }
            />
          ) : (
            "No image"
          )}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap="8px">
          <IconButton
            onClick={() => handleEditClick(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: colors.orange[400] }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <img
          src={Nodata}
          alt="No data"
          style={{ width: 150, marginBottom: 16 }}
        />
        <Typography variant="h6">No data available</Typography>
      </Box>
    );
  };

  return (
    <Box m={"10px"}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Add/Edit Dialog */}
      <Dialog
        open={openEditDialog || openAddDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setOpenAddDialog(false);
          setSelectedProject(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedProject ? "Edit Project" : "Add New Project"}
        </DialogTitle>
        <DialogContent>
          <Form
            onSubmit={handleSubmit}
            style={{ width: "100%", padding: "1rem" }}
          >
            <Box
              display={"flex"}
              gap={"1rem"}
              width={"100%"}
              mb={"1rem"}
              flexWrap="wrap"
            >
              {/* Project Type */}
              <FormControl variant="standard" style={{ width: "100%" }}>
                <InputLabel id="project-type-label">Project Type *</InputLabel>
                <Select
                  labelId="project-type-label"
                  id="project_type"
                  name="project_type"
                  value={formData.project_type}
                  onChange={handleFormChange}
                  required
                >
                  <MenuItem value="">
                    <em>Select Project Type</em>
                  </MenuItem>
                  {projectTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Department */}
              <TextField
                select
                style={{ width: "100%" }}
                id="department"
                name="department"
                label="Department"
                variant="standard"
                value={formData.department}
                onChange={handleFormChange}
                required
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                {department.map((dep) => (
                  <option key={dep.id} value={dep.id}>
                    {dep.department_name}
                  </option>
                ))}
              </TextField>

              {/* Project Name */}
              <TextField
                style={{ width: "100%" }}
                id="name"
                name="name"
                label="Project Name"
                variant="standard"
                value={formData.name}
                onChange={handleFormChange}
                required
              />

              {/* Description */}
              <TextField
                style={{ width: "100%" }}
                id="description"
                name="description"
                label="Description"
                multiline
                minRows={2}
                maxRows={12}
                variant="standard"
                value={formData.description}
                onChange={handleFormChange}
                required
              />

              {/* General Link */}
              <TextField
                style={{ width: "100%" }}
                id="general_link"
                name="general_link"
                label="General Link (Web URL)"
                variant="standard"
                value={formData.general_link}
                onChange={handleFormChange}
                placeholder="https://example.com"
              />

              {/* Android Link */}
              <TextField
                style={{ width: "100%" }}
                id="android_link"
                name="android_link"
                label="Android App Link"
                variant="standard"
                value={formData.android_link}
                onChange={handleFormChange}
                placeholder="https://play.google.com/store/apps/details?id=com.example"
              />

              {/* iOS Link */}
              <TextField
                style={{ width: "100%" }}
                id="ios_link"
                name="ios_link"
                label="iOS App Link"
                variant="standard"
                value={formData.ios_link}
                onChange={handleFormChange}
                placeholder="https://apps.apple.com/app/id123456789"
              />

              {/* Project Image */}
              <Box width={"100%"}>
                <label>Project Image</label>
                <Input
                  type="file"
                  style={{ width: "100%" }}
                  id="image"
                  name="image"
                  onChange={handleFileChange}
                  required={!selectedProject}
                />
                {selectedProject &&
                  formData.image &&
                  typeof formData.image === "string" && (
                    <Typography variant="caption" color={colors.green[400]}>
                      Current image: {formData.image}
                    </Typography>
                  )}
              </Box>
            </Box>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditDialog(false);
              setOpenAddDialog(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            sx={{ backgroundColor: colors.blue[400] }}
            variant="contained"
            endIcon={<SendIcon />}
          >
            {selectedProject ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{projectToDelete?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            sx={{ backgroundColor: colors.red[400] }}
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        mb={"30px"}
        ml={"15px"}
        mr={"15px"}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Box flex={1}>
          <Typography
            variant="h4"
            color={colors.dark[400]}
            fontWeight={"bold"}
            sx={{ mb: "5px" }}
          >
            Projects Management
          </Typography>
          <Typography variant="h5" color={colors.blue[400]}>
            Manage offered projects
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{
            backgroundColor: colors.blue[400],
            "&:hover": {
              backgroundColor: colors.blue[500],
            },
          }}
        >
          Add Project
        </Button>
      </Box>
      <Divider component="" />
      <Box
        sx={{ height: "45vh", width: "100%" }}
        backgroundColor={colors.dark[300]}
        padding={".2rem"}
        mt={"1rem"}
      >
        <DataGrid
          rows={projects}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          sx={{
            border: "none",
            ".MuiDataGrid-cell": {
              borderBottom: "none",
              backgroundColor: "none",
            },
            ".MuiDataGrid-toolbarContainer": {
              padding: "10px",
              backgroundColor: colors.primary[900],
              borderBottom: "none",
            },
            ".MuiDataGrid-quickFilter": {
              padding: "8px",
              borderRadius: "4px",
              border: `1px solid ${colors.primary[100]}`,
            },
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: colors.primary[900],
            },
            ".MuiDataGrid-footerContainer": {
              backgroundColor: colors.primary[900],
            },
          }}
          slots={{
            toolbar: () => (
              <div>
                <GridToolbarQuickFilter />
              </div>
            ),
            noRowsOverlay: NoDataOverlay,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 500,
                placeholder: "Search...",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Projects;
